import { type PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import type { Asset, AssetCategory } from "@/Dashboard/pages/AssetLibrary/types";
import { areAllFiltersInactive, filterAssets } from "@/Dashboard/pages/AssetLibrary/utils/assetLibraryFilters";
import { sortAssets } from "@/Dashboard/pages/AssetLibrary/utils/assetLibrarySort";
import { REQUEST_STATUSES } from "@/constants/requestStatuses";
import { OrganizationsService } from "@/services/OrganizationsService";
import type { State } from "@/store/types";

import type { AssetFiltersState } from "../assetFilters/types";

interface Data {
  propertyId: number;
  propertyName: string;
  assets: Asset[];
  assetCategories: AssetCategory[];
}

export interface OrganizationAssetsState {
  organizationAssets: Data[];
  organizationAssetsFiltered: Data[];
  status: REQUEST_STATUSES;
  error: unknown;
}

const initialState: OrganizationAssetsState = {
  organizationAssets: [],
  organizationAssetsFiltered: [],
  status: REQUEST_STATUSES.IDLE,
  error: null,
};

export const fetchOrganizationAssets = createAsyncThunk(
  "organizationAssets/fetchOrganizationAssets",
  async ({ organizationId }: { organizationId: number }, { rejectWithValue }) => {
    try {
      const responses = await Promise.all([
        OrganizationsService.getAssets(organizationId),
        OrganizationsService.getAssetCategories(organizationId),
      ]);

      const combined: Record<number, Data> = {};
      for (const data of responses[0].resp.data) {
        combined[data.propertyId] ||= { propertyId: data.propertyId, propertyName: data.propertyName } as Data;
        combined[data.propertyId].assets ||= [];
        combined[data.propertyId].assets = combined[data.propertyId].assets.concat(data.mediaItems);
      }
      for (const data of responses[1].resp.data) {
        combined[data.propertyId] ||= { propertyId: data.propertyId, propertyName: data.propertyName } as Data;
        combined[data.propertyId].assetCategories ||= [];
        combined[data.propertyId].assetCategories = combined[data.propertyId].assetCategories.concat(
          data.assetCategories,
        );
      }

      return Object.values(combined);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const filterOrganizationAssets = createAsyncThunk(
  "organizationAssets/filterOrganizationAssets",
  ({
    filters,
    organizationAssets,
    showFilters,
    sortField,
    sortOrientation,
  }: {
    filters: AssetFiltersState["filters"];
    organizationAssets: Data[];
    showFilters: boolean;
    sortField: string;
    sortOrientation: string;
  }) => {
    const data: Data[] = [];
    for (const organizationAsset of organizationAssets) {
      const filtered = filterAssets(
        organizationAsset.assets,
        showFilters ? filters : { searchText: filters.searchText },
      );
      const sorted = sortAssets(filtered, sortField, sortOrientation);

      // we should show all properties/categories if no filters are active
      const noFiltersActive = areAllFiltersInactive(filters);

      const assetCategories = noFiltersActive
        ? organizationAsset.assetCategories
        : organizationAsset.assetCategories.filter((x) => sorted.some((y) => y.assetCategoryId === x.id));

      if (assetCategories.length > 0 || sorted.length > 0 || noFiltersActive) {
        data.push({
          assetCategories,
          assets: sorted,
          propertyId: organizationAsset.propertyId,
          propertyName: organizationAsset.propertyName,
        });
      }
    }

    return data;
  },
);

const organizationAssetsSlice = createSlice({
  name: "organizationAssets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationAssets.pending, (state) => {
        state.status = REQUEST_STATUSES.LOADING;
        state.error = null;
      })
      .addCase(fetchOrganizationAssets.fulfilled, (state, action: PayloadAction<Data[]>) => {
        state.organizationAssets = action.payload || [];
        state.status = REQUEST_STATUSES.SUCCEEDED;
      })
      .addCase(fetchOrganizationAssets.rejected, (state, action) => {
        state.status = REQUEST_STATUSES.FAILED;
        state.error = action.payload;
      })
      .addCase(filterOrganizationAssets.fulfilled, (state, action) => {
        state.organizationAssetsFiltered = action.payload;
      });
  },
});

export default organizationAssetsSlice.reducer;

export const organizationAssetsSelector = createSelector(
  (state: State) => state.organizationAssets,
  (organizationAssets) => ({
    ...initialState,
    ...organizationAssets,
  }),
);
